'use client';

import { handleApiErrors } from '@wolfejs/core/utils/handleApiErrors';
import type { BuyerBusinessEntity, BuyerBusinessUpdatePayload } from '@wolfejs/ms-api-js/src/types/buyers';
import { useAdminBuyerUpdateByBusinessId } from '@wolfejs/ms-api-react/src/hooks/admin/buyers';
import { Form } from '@wolfejs/ui/Form/Form';
import type { FormSchemaEntry } from '@wolfejs/ui/Form/types';
import { Loading } from '@wolfejs/ui/Loading';
import { UI } from '@wolfejs/ui/UI';
import type { UIComponentProps } from '@wolfejs/ui/types/UI';
import { cn } from '@wolfejs/ui/utils/classnames';
import { z } from 'zod';

const schema: FormSchemaEntry[] = [
  {
    label: 'Business contact name',
    name: 'name',
    type: 'text',
    validation: z.string().min(2),
    showRequiredIndicator: true,
  },
  {
    label: 'Full address',
    name: 'fullAddress',
    type: 'text',
    validation: z.string().min(2),
    showRequiredIndicator: true,
  },
  {
    label: 'Business contact email',
    name: 'email',
    type: 'email',
    validation: z.string().email(),
    showRequiredIndicator: true,
  },
  {
    label: 'Department',
    name: 'department',
    type: 'text',
  },
  {
    label: 'Zip/Postal code',
    name: 'zip',
    type: 'text',
  },
  {
    label: 'Business contact phone number',
    name: 'phoneNumber',
    type: 'phone',
    showRequiredIndicator: true,
  },
  {
    label: 'City',
    name: 'city',
    type: 'text',
  },
];

export type AdminGeneralInfoFormValues = BuyerBusinessEntity & {
  name: string;
  email: string;
  organization: string;
  zip: string;
  department: string;
  country: string;
  phoneNumber: string;
  city: string;
  fullAddress: string;
};

export type AdminGeneralInfoFormProps = UIComponentProps & {
  defaultValues?: Object | AdminGeneralInfoFormValues;
  buyerData?: BuyerBusinessEntity;
};

export const AdminGeneralInfoForm = ({ className, buyerData }: AdminGeneralInfoFormProps) => {
  const updateBuyer = useAdminBuyerUpdateByBusinessId();

  // console.log('buyerData', buyerData);

  const onSubmit = async (data: AdminGeneralInfoFormValues) => {
    // console.log('onSubmit buyerData', buyerData);

    if (!buyerData) return;
    if (!buyerData.userBusinessMapping?.[0]?.businessId) {
      console.error('Buyer is not connected to a business');
      return;
    }

    const { address } = buyerData as BuyerBusinessEntity;
    const { name, email, zip, department, country, phoneNumber, city, fullAddress } = data;
    const updateData: BuyerBusinessUpdatePayload = {
      id: buyerData.userBusinessMapping?.[0]?.businessId,
      business: {
        name,
        // email // NOTE: user should not be allowed to update email like this
        department,
        phoneNumber,
        address: {
          ...address,
          street: fullAddress,
          city,
          zipCode: zip,
          state: address?.state || '',
          country,
        },
      },
    };
    try {
      await updateBuyer.mutateAsync(updateData);
      UI.notifications.success('The buyer information has been successfully updated.');
    } catch (error) {
      handleApiErrors(error);
      return;
    }
  };

  if (!buyerData) return <Loading />;

  return (
    <Form
      schema={schema}
      onSubmit={onSubmit}
      defaultValues={{
        name: buyerData.name,
        email: buyerData.email,
        fullAddress: `${buyerData.address?.street ?? ''} ${buyerData.address?.street2 ?? ''}`.trim(),
        city: buyerData.address?.city,
        zip: buyerData.address?.zipCode,
        phoneNumber: buyerData?.phoneNumber,
        department: buyerData?.department,
      }}
      hasSubmitButton
      submitButtonText="Save changes"
      fieldsetLayoutClasses={cn(
        'grid grid-cols-1 gap-4',
        // breakpoints
        '@sm:grid-cols-2'
      )}
      className={cn('@container', className)}
      submitButtonProps={{
        loading: updateBuyer.isLoading,
      }}
    />
  );
};
